/**
 * Navigate search - pure ranking, no Solid, no DOM.
 *
 * The novice/pro split lives here as much as in the layout: a novice types
 * the word THEY use ("pedal", "dmx", "backup") and lands on the thing the app
 * calls something else (Connected Devices, Lights, Settings > Export); a pro
 * types two letters and presses Enter. Both come from the same rule - match
 * the title first, then the keywords, then a loose in-order subsequence.
 */

export type SearchKind = 'dest' | 'action' | 'section' | 'project' | 'timeline' | 'page' | 'tool';

export interface SearchEntry {
  id: string;
  kind: SearchKind;
  title: string;
  /** One line under the title: what choosing it does. */
  detail: string;
  /** Where it lives, shown at the right: teaches the map while searching. */
  where: string;
  /** Space-separated extra words, lower case: synonyms in the user's vocabulary. */
  keywords?: string;
}

/** Earlier kinds win ties: a destination beats a Settings section with the
 *  same score, which beats one of the operator's own named things. */
const KIND_ORDER: Record<SearchKind, number> = {
  dest: 0, action: 1, section: 2, tool: 3, project: 4, timeline: 5, page: 6,
};

/** 0 = no match. Higher is better. One query WORD against one entry. */
export function scoreWord(entry: SearchEntry, word: string): number {
  const t = entry.title.toLowerCase();
  const kw = (entry.keywords ?? '').toLowerCase();
  if (!word) return 0;
  if (t === word) return 100;
  if (t.startsWith(word)) return 90;
  if (t.split(/[\s\-/]+/).some((w) => w.startsWith(word))) return 80;
  if (kw.split(/\s+/).some((w) => w.startsWith(word))) return 70;
  if (t.includes(word)) return 60;
  if (kw.includes(word)) return 50;
  // Loose subsequence on the title ("tml" -> Timelines), the last resort.
  let i = 0;
  for (const ch of t) {
    if (ch === word[i]) i += 1;
    if (i === word.length) return 30;
  }
  return 0;
}

/** Every word must match (AND); an entry scores as its weakest word. */
export function rankEntries(entries: readonly SearchEntry[], query: string, limit = 12): SearchEntry[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const scored: Array<{ e: SearchEntry; s: number; i: number }> = [];
  entries.forEach((e, i) => {
    let s = Infinity;
    for (const w of words) {
      s = Math.min(s, scoreWord(e, w));
      if (s === 0) break;
    }
    if (s > 0 && s !== Infinity) scored.push({ e, s, i });
  });
  scored.sort((a, b) => b.s - a.s || KIND_ORDER[a.e.kind] - KIND_ORDER[b.e.kind] || a.i - b.i);
  return scored.slice(0, limit).map((x) => x.e);
}

/** Where the FIRST query word sits inside the title, for highlighting. */
export function matchRange(title: string, query: string): [number, number] | null {
  const word = query.trim().toLowerCase().split(/\s+/)[0] ?? '';
  if (!word) return null;
  const at = title.toLowerCase().indexOf(word);
  return at < 0 ? null : [at, at + word.length];
}

/** "now", "5m", "3h", "2d", "3w", "4mo" - the compact age a recent-project row shows. */
export function shortAge(ms: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return 'now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d}d`;
  const w = Math.round(d / 7);
  if (w < 9) return `${w}w`;
  return `${Math.round(d / 30)}mo`;
}
